// This is a COPY of downread/shared/src/schema/readmarker-helper.ts. To modify, edit the original and then run downread/bin/build-shared.sh
import { RestReadMarkerPatch, FeedReadMarkerPatch, PostMarker, PostStarPatch, MAX_ACTIVE_POSTS } from './api-schema';

/**
 * Normalizes a PostMarker array by:
 * 1. Expanding all markers into individual indices
 * 2. Sorting them
 * 3. Merging adjacent/overlapping indices into optimized ranges
 */
function normalizeMarkers(markers: PostMarker[] | undefined): PostMarker[] {
    if (!markers || !markers.length) return [];

    // Expand all markers to individual indices
    const indices = new Set<number>();
    for (const marker of markers) {
        if (typeof marker == 'number') {
            indices.add(marker);
        } else {
            const [start, end] = marker;
            for (let i = start; i <= end; i++) {
                indices.add(i);
            }
        }
    }

    // Sort indices
    const sorted = Array.from(indices).sort((a, b) => a - b);
    if (!sorted.length) return [];

    // Merge into optimized ranges
    const result: PostMarker[] = [];
    let rangeStart = sorted[0];
    let rangeEnd = sorted[0];

    for (let i = 1; i < sorted.length; i++) {
        if (sorted[i] == rangeEnd + 1) {
            // Extend current range
            rangeEnd = sorted[i];
        } else {
            // Save current range and start new one
            if (rangeStart == rangeEnd) {
                result.push(rangeStart);
            } else if (rangeEnd - rangeStart == 1) {
                // Only two consecutive numbers, use individual markers
                result.push(rangeStart, rangeEnd);
            } else {
                result.push([rangeStart, rangeEnd]);
            }
            rangeStart = sorted[i];
            rangeEnd = sorted[i];
        }
    }

    // Save last range
    if (rangeStart == rangeEnd) {
        result.push(rangeStart);
    } else if (rangeEnd - rangeStart == 1) {
        // Only two consecutive numbers, use individual markers
        result.push(rangeStart, rangeEnd);
    } else {
        result.push([rangeStart, rangeEnd]);
    }

    return result;
}

/**
 * Removes specific indices from a marker array
 */
function removeIndicesFromMarkers(markers: PostMarker[] | undefined, toRemove: PostMarker[] | undefined): PostMarker[] {
    if (!markers || !markers.length) return [];
    if (!toRemove || !toRemove.length) return markers;

    // Get all indices to remove
    const removeSet = new Set<number>();
    for (const marker of toRemove) {
        if (typeof marker == 'number') {
            removeSet.add(marker);
        } else {
            const [start, end] = marker;
            for (let i = start; i <= end; i++) {
                removeSet.add(i);
            }
        }
    }

    // Expand markers to indices, filter out removed ones
    const indices: number[] = [];
    for (const marker of markers) {
        if (typeof marker == 'number') {
            if (!removeSet.has(marker)) {
                indices.push(marker);
            }
        } else {
            const [start, end] = marker;
            for (let i = start; i <= end; i++) {
                if (!removeSet.has(i)) {
                    indices.push(i);
                }
            }
        }
    }

    if (!indices.length) return [];
    return normalizeMarkers(indices);
}

/**
 * Determines the effective markAllRead level when combining two values
 */
function mergeMarkAllRead(
    existing: FeedReadMarkerPatch['markAllRead'],
    update: FeedReadMarkerPatch['markAllRead']
): FeedReadMarkerPatch['markAllRead'] {
    if (update == 'all') return 'all';
    if (!update) return existing;
    if (existing == 'all') return 'all';
    return update;
}

/**
 * Merges two FeedReadMarkerPatch objects for the same feed
 */
export function mergeFeedPatches(existing: FeedReadMarkerPatch, update: FeedReadMarkerPatch): FeedReadMarkerPatch {
    const result: FeedReadMarkerPatch = {};

    // Merge settings: update takes precedence (only include if defined)
    const order = update.order ?? existing.order;
    if (order) result.order = order;
    const showRead = update.showRead ?? existing.showRead;
    if (showRead) result.showRead = showRead;
    const compactMode = update.compactMode ?? existing.compactMode;
    if (compactMode) result.compactMode = compactMode;
    // 0 means "clear the limit", so merge on != undefined, not truthiness
    const maxUnread = update.maxUnread ?? existing.maxUnread;
    if (maxUnread != undefined) result.maxUnread = maxUnread;
    const maxAgeDays = update.maxAgeDays ?? existing.maxAgeDays;
    if (maxAgeDays != undefined) result.maxAgeDays = maxAgeDays;
    const pausedUntil = update.pausedUntil ?? existing.pausedUntil;
    if (pausedUntil != undefined) result.pausedUntil = pausedUntil;

    // Merge markAllRead, keeping the postCount that belongs to the markAllRead that wins
    const markAllRead = mergeMarkAllRead(existing.markAllRead, update.markAllRead);
    if (markAllRead) {
        result.markAllRead = markAllRead;
        const postCount = update.markAllRead == markAllRead ? update.postCount : existing.postCount;
        if (postCount != undefined) result.postCount = postCount;
    }

    // If result has markAllRead == 'all', we can ignore all read/unread markers
    if (markAllRead == 'all') {
        // Only keep unread markers from update (they override the "all read")
        if (update.markAsUnread && update.markAsUnread.length > 0) {
            result.markAsUnread = normalizeMarkers(update.markAsUnread);
        }
        return result;
    }

    // Combine markAsRead from both, but remove any that update marks as unread
    let combinedRead = normalizeMarkers([
        ...(existing.markAsRead || []),
        ...(update.markAsRead || [])
    ]);

    // Remove indices that update marks as unread
    if (update.markAsUnread && update.markAsUnread.length > 0) {
        combinedRead = removeIndicesFromMarkers(combinedRead, update.markAsUnread);
    }

    if (combinedRead.length > 0) {
        result.markAsRead = combinedRead;
    }

    // Combine markAsUnread from both, but remove any that update marks as read
    let combinedUnread = normalizeMarkers([
        ...(existing.markAsUnread || []),
        ...(update.markAsUnread || [])
    ]);

    // Remove indices that update marks as read (from update only, not from markAllRead)
    if (update.markAsRead && update.markAsRead.length > 0) {
        combinedUnread = removeIndicesFromMarkers(combinedUnread, update.markAsRead);
    }

    if (combinedUnread.length > 0) {
        result.markAsUnread = combinedUnread;
    }

    return result;
}

/**
 * Merges two RestReadMarkerPatch objects.
 *
 * The result, when applied to a database, will have the same effect as applying
 * 'existing' first and then 'update'.
 *
 * @param existing - The first patch to apply
 * @param update - The second patch to apply
 * @returns A merged patch that combines both operations
 */
export function mergeReadMarkerPatches(
    existing: RestReadMarkerPatch,
    update: RestReadMarkerPatch
): RestReadMarkerPatch {
    const result: RestReadMarkerPatch = {};

    // Get all feed IDs from both patches
    const allFeedIds = Array.from(new Set([
        ...Object.keys(existing),
        ...Object.keys(update)
    ]));

    for (const feedId of allFeedIds) {
        const existingPatch = existing[feedId];
        const updatePatch = update[feedId];

        if (existingPatch && updatePatch) {
            // Both have this feed, merge them
            result[feedId] = mergeFeedPatches(existingPatch, updatePatch);
        } else if (updatePatch) {
            // Only update has this feed
            result[feedId] = { ...updatePatch };
        } else {
            // Only existing has this feed
            result[feedId] = { ...existingPatch };
        }
    }

    return result;
}

// Read bitmaps wire/storage form ------------------------------

/** Per-feed read bitmaps are sent/stored as a sparse Record (feedId -> base64) instead of an
 *  array aligned 1:1 with feedMarkers once MORE than this fraction of them are empty. Bitmaps
 *  are empty for every feed whose read state isn't a bitfield ('b') marker — the common case —
 *  so the sparse form (which omits the empties) usually shrinks the payload substantially. */
export const SPARSE_BITMAP_EMPTY_RATIO = 0.7;

/** feedId is the part of a feed-marker string before the first '/'. */
function feedIdOfMarker(marker: string): string {
    const slash = marker.indexOf('/');
    return slash < 0 ? marker : marker.substring(0, slash);
}

/**
 * Encodes per-feed base64 bitmaps (aligned 1:1 with feedMarkers) into the wire/storage form:
 * the plain array, or — when more than SPARSE_BITMAP_EMPTY_RATIO of them are empty — a sparse
 * Record keyed by feedId with the empties omitted. Inverse of {@link decodeBitmaps}.
 */
export function encodeBitmaps(base64Bitmaps: string[], feedMarkers: string[]): string[] | Record<string, string> {
    if (!base64Bitmaps.length) return base64Bitmaps;
    let empty = 0;
    for (const b of base64Bitmaps) if (!b) empty++;
    if (empty / base64Bitmaps.length <= SPARSE_BITMAP_EMPTY_RATIO) return base64Bitmaps;
    const record: Record<string, string> = {};
    for (let i = 0; i < base64Bitmaps.length; i++)
        if (base64Bitmaps[i]) record[feedIdOfMarker(feedMarkers[i])] = base64Bitmaps[i];
    return record;
}

/**
 * Inverse of {@link encodeBitmaps}: returns a base64 bitmap array aligned 1:1 with feedMarkers,
 * whether the input was already an array or a sparse feedId-keyed Record (empties become '').
 */
export function decodeBitmaps(bitmaps: string[] | Record<string, string>, feedMarkers: string[]): string[] {
    if (Array.isArray(bitmaps)) return bitmaps;
    return feedMarkers.map(m => bitmaps[feedIdOfMarker(m)] ?? '');
}

// Auto-mark-read limits ------------------------------

/** Allowed values for FeedReadMarkerPatch.maxUnread (besides 0 = off) */
export const MAX_UNREAD_CHOICES = [10, 20, 50, 100];
/** Allowed values for FeedReadMarkerPatch.maxAgeDays (besides 0 = off) */
export const MAX_AGE_DAYS_CHOICES = [1, 2, 3, 7, 14, 30];

const MS_PER_DAY = 24 * 60 * 60 * 1000;
/** Semis sent to clients are trimmed to this window (must cover the largest age choice) */
const SEMI_TRIM_DAYS = 31;

/**
 * Computes the highest post index that the auto-read limits allow to be marked read, or -1 if
 * nothing may be marked. Marking [0..result] satisfies both limits.
 *
 * @param postCount Current post count
 * @param semis Snapshot of recent post semi-feed-times, newest first (see RestUserFeed.recentPostsSemi)
 * @param semisPostCount Post count at snapshot time: semis[i] belongs to index (semisPostCount-1-i).
 *        Posts newer than the snapshot are never marked by the age rule (they are fresh by definition).
 * @param maxUnread Keep at most this many newest posts unread; 0/undefined = no count limit
 * @param maxAgeDays Posts older than this many days are marked read; 0/undefined = no age limit
 */
export function computeAutoReadBoundary(postCount: number, semis: number[] | undefined,
        semisPostCount: number, maxUnread: number | undefined, maxAgeDays: number | undefined,
        nowMs: number): number {
    if (!postCount) return -1;

    // Count rule: everything older than the maxUnread newest posts
    const boundaryCount = maxUnread ? postCount - 1 - maxUnread : -1;

    // Age rule: first snapshot entry older than the cutoff (semis are monotonic descending)
    let boundaryAge = -1;
    if (maxAgeDays && semis?.length) {
        const cutoffSemi = Math.floor((nowMs - maxAgeDays * MS_PER_DAY) / 10000);
        const i = semis.findIndex(semi => semi < cutoffSemi);
        if (i >= 0) boundaryAge = semisPostCount - 1 - i;
    }

    return Math.min(Math.max(boundaryCount, boundaryAge), postCount - 1);
}

// Pausing ------------------------------

/** FeedReadMarkerPatch.pausedUntil value for a pause without end (a semi far in the future, ~2650) */
export const PAUSED_INDEFINITELY = 2 ** 31 - 1;
/** Pause durations offered in the UI */
export const PAUSE_DAYS_CHOICES = [7, 30];

/** Current time as semi (ms/10000) */
export const nowSemi = (nowMs = Date.now()) => Math.floor(nowMs / 10000);

/** Semi time a pause of the given length ends, counted from `fromSemi` (now when omitted or past). */
export function pauseEndSemi(days: number, fromSemi?: number, nowMs = Date.now()): number {
    return Math.max(fromSemi ?? 0, nowSemi(nowMs)) + days * MS_PER_DAY / 10000;
}

/**
 * For a pause that ended at `untilSemi`: the highest post index that arrived before the end (and
 * should therefore be read), -1 if none did, or undefined if the snapshot can't tell (it doesn't
 * reach back to the pause end). semis are newest first, see computeAutoReadBoundary.
 */
export function computePauseEndBoundary(semis: number[], semisPostCount: number, untilSemi: number): number | undefined {
    const i = semis.findIndex(semi => semi < untilSemi);
    if (i >= 0) return semisPostCount - 1 - i;
    return semis.length >= semisPostCount ? -1 : undefined;
}

/** Keeps the semis newer than cutoffSemi plus one older sentinel entry (see trimSemisForLimits). */
export function trimSemisSince(semis: number[], cutoffSemi: number): number[] {
    const firstOld = semis.findIndex(semi => semi < cutoffSemi);
    return firstOld < 0 ? semis : semis.slice(0, firstOld + 1);
}

/**
 * Trims a lastPostsSemi array for transfer to the client: keeps entries newer than the window plus
 * one older sentinel entry. The sentinel lets the age rule find its boundary even when all
 * remaining posts are older than the window; everything below is covered by the marked range.
 *
 * The window is the feed's own maxAgeDays (+ a one-day buffer for clock skew / a stale tab) when
 * given, so a high-frequency feed with a 1-day limit no longer ships ~31 days of post times. When
 * maxAgeDays is omitted (e.g. the limits-preview UI, which may probe any age choice) it falls back
 * to SEMI_TRIM_DAYS, which covers the largest selectable age.
 */
export function trimSemisForLimits(semis: number[], nowMs: number, maxAgeDays?: number): number[] {
    return trimSemisSince(semis, limitsCutoffSemi(nowMs, maxAgeDays));
}

/** The oldest semi trimSemisForLimits keeps (apart from the sentinel). */
export function limitsCutoffSemi(nowMs: number, maxAgeDays?: number): number {
    const windowDays = maxAgeDays ? maxAgeDays + 1 : SEMI_TRIM_DAYS;
    return Math.floor((nowMs - windowDays * MS_PER_DAY) / 10000);
}

/** How many newest posts an anonymous client keeps unread when it follows a feed for the first
 *  time: at least the 10 newest, plus everything posted in the last 72h, but never more than 100
 *  (and never more than the feed actually has). The older posts are marked read on subscribe so a
 *  long-running feed doesn't dump hundreds of unread items on the user. lastPostsSemi is
 *  newest-first (ms/10000). Returns the count of newest posts to leave unread. */
export function calculateAnonInitialUnread(lastPostsSemi: number[] | undefined, postCount: number, nowMs: number = Date.now(), maxKeep: number = 100, maxAgeHours: number = 72): number {
    if (postCount <= 0) return 0;
    const cutoffSemi = Math.floor((nowMs - maxAgeHours * 60 * 60 * 1000) / 10000);
    let recentCount = 0;
    for (const semi of lastPostsSemi ?? []) {
        if (semi >= cutoffSemi) recentCount++;
        else break; // newest-first, so the rest are older
    }
    return Math.min(postCount, Math.min(maxKeep, Math.max(10, recentCount)));
}

/**
 * Parses star/readLater entry strings into a map of feedId -> Set<postIndex>
 */
export function parseStarEntries(entries: string[] | undefined): Map<string, Set<number>> {
    const result = new Map<string, Set<number>>();
    if (!entries) return result;

    for (const entry of entries) {
        const slashIndex = entry.indexOf('/');
        const feedId = entry.substring(0, slashIndex);
        const rest = entry.substring(slashIndex + 1);

        if (!result.has(feedId)) {
            result.set(feedId, new Set());
        }
        const set = result.get(feedId)!;

        const dashIndex = rest.indexOf('-');
        if (dashIndex == -1) {
            // Single post
            set.add(parseInt(rest, 10));
        } else {
            // Range
            const start = parseInt(rest.substring(0, dashIndex), 10);
            const end = parseInt(rest.substring(dashIndex + 1), 10);
            for (let i = start; i <= end; i++) {
                set.add(i);
            }
        }
    }
    return result;
}

/**
 * Converts a map of feedId -> Set<postIndex> back to optimized string entries
 */
function serializeStarEntries(map: Map<string, Set<number>>): string[] {
    const result: string[] = [];

    for (const [feedId, indices] of Array.from(map)) {
        if (indices.size == 0) continue;

        const sorted = Array.from(indices).sort((a, b) => a - b);
        let rangeStart = sorted[0];
        let rangeEnd = sorted[0];

        for (let i = 1; i < sorted.length; i++) {
            if (sorted[i] == rangeEnd + 1) {
                rangeEnd = sorted[i];
            } else {
                // Save current range
                if (rangeStart == rangeEnd) {
                    result.push(`${feedId}/${rangeStart}`);
                } else if (rangeEnd - rangeStart == 1) {
                    result.push(`${feedId}/${rangeStart}`);
                    result.push(`${feedId}/${rangeEnd}`);
                } else {
                    result.push(`${feedId}/${rangeStart}-${rangeEnd}`);
                }
                rangeStart = sorted[i];
                rangeEnd = sorted[i];
            }
        }

        // Save last range
        if (rangeStart == rangeEnd) {
            result.push(`${feedId}/${rangeStart}`);
        } else if (rangeEnd - rangeStart == 1) {
            result.push(`${feedId}/${rangeStart}`);
            result.push(`${feedId}/${rangeEnd}`);
        } else {
            result.push(`${feedId}/${rangeStart}-${rangeEnd}`);
        }
    }

    return result;
}

/**
 * Merges two PostStarPatch objects.
 *
 * The result, when applied to a database, will have the same effect as applying
 * 'existing' first and then 'update'.
 *
 * @param existing - The first patch to apply
 * @param update - The second patch to apply
 * @returns A merged patch that combines both operations
 */
export function mergePostStarPatches(
    existing: PostStarPatch,
    update: PostStarPatch
): PostStarPatch {
    // If update has removeAll, it overrides everything
    if (update.removeAll) {
        return { ...update };
    }

    // Parse entries into maps
    const addedMap = parseStarEntries(existing.added);
    const removedMap = parseStarEntries(existing.removed);
    const updateAddedMap = parseStarEntries(update.added);
    const updateRemovedMap = parseStarEntries(update.removed);

    // If existing has removeAll, start with empty state
    if (existing.removeAll) {
        // After existing: all feeds cleared, then existing.added applied
        // Apply update operations on top
        for (const [feedId, toRemove] of Array.from(updateRemovedMap)) {
            const current = addedMap.get(feedId);
            if (current) {
                for (const idx of Array.from(toRemove)) {
                    current.delete(idx);
                }
                if (current.size == 0) {
                    addedMap.delete(feedId);
                }
            }
        }

        for (const [feedId, toAdd] of Array.from(updateAddedMap)) {
            if (!addedMap.has(feedId)) {
                addedMap.set(feedId, new Set());
            }
            const current = addedMap.get(feedId)!;
            for (const idx of Array.from(toAdd)) {
                current.add(idx);
            }
        }

        const result: PostStarPatch = { removeAll: true };
        if (addedMap.size > 0) {
            result.added = serializeStarEntries(addedMap);
        }
        return result;
    }

    // Normal case: no removeAll in existing
    // Remove update.removed from existing.added
    for (const [feedId, toRemove] of Array.from(updateRemovedMap)) {
        const current = addedMap.get(feedId);
        if (current) {
            for (const idx of Array.from(toRemove)) {
                current.delete(idx);
            }
            if (current.size == 0) {
                addedMap.delete(feedId);
            }
        }
    }

    // Add update.added to existing.added
    for (const [feedId, toAdd] of Array.from(updateAddedMap)) {
        if (!addedMap.has(feedId)) {
            addedMap.set(feedId, new Set());
        }
        const current = addedMap.get(feedId)!;
        for (const idx of Array.from(toAdd)) {
            current.add(idx);
        }
    }

    // Remove update.added from existing.removed
    for (const [feedId, toAdd] of Array.from(updateAddedMap)) {
        const current = removedMap.get(feedId);
        if (current) {
            for (const idx of Array.from(toAdd)) {
                current.delete(idx);
            }
            if (current.size == 0) {
                removedMap.delete(feedId);
            }
        }
    }

    // Add update.removed to existing.removed
    for (const [feedId, toRemove] of Array.from(updateRemovedMap)) {
        if (!removedMap.has(feedId)) {
            removedMap.set(feedId, new Set());
        }
        const current = removedMap.get(feedId)!;
        for (const idx of Array.from(toRemove)) {
            current.add(idx);
        }
    }

    const result: PostStarPatch = {};
    if (addedMap.size > 0) {
        result.added = serializeStarEntries(addedMap);
    }
    if (removedMap.size > 0) {
        result.removed = serializeStarEntries(removedMap);
    }

    return result;
}

// ============================================================================
// Feed marker format — parse + read-set
// Format: <feedId>/<sortOrder>/<viewMode>/<mode>/<mode-data>
// viewMode: <showRead><compactMode>[m<maxUnread>][a<maxAgeDays>][p[<pausedUntil>]]
// These are pure helpers (no DB/Node deps) so both the backend and the client can read
// markers. The write-path serializers (serializeMarker/readSetToMarker) stay in api/lib/db-users.
// ============================================================================

/** Maximum number of posts to track read state for. Older posts are not shown. (== MAX_ACTIVE_POSTS) */
export const MAX_TRACKED_POSTS = MAX_ACTIVE_POSTS;

export type MarkerMode = 'r' | '0' | 't' | 'b';
export type ShowRead = 'u' | 'a';
export type CompactMode = 'c' | 'f' | 'l';
export type MarkerSortOrder = 'N' | 'O' | 'A' | '?';

export interface ParsedMarker {
    feedId: string;
    sortOrder: MarkerSortOrder;
    showRead: ShowRead;
    compactMode: CompactMode;
    // Auto-mark-read limits; undefined = off
    maxUnread?: number;
    maxAgeDays?: number;
    // Semi time the pause ends (PAUSED_INDEFINITELY = no end); undefined = not paused
    pausedUntil?: number;
    mode: MarkerMode;
    // Mode 'r' (read until): posts 0 to readUntilIndex are read
    readUntilIndex?: number;
    // Mode 't' (read to): posts 0 to readToIndex-1 are read
    readToIndex?: number;
    // Mode 'b' (bitfield): first >= last, first bit = highest index
    firstPostInBitField?: number;
    lastPostInBitField?: number;
    restRead?: boolean;
}

/** The pause end of a marker's viewMode part: 'p<semi>', or a bare 'p' for PAUSED_INDEFINITELY. */
export function parsePausedUntil(viewMode: string): number | undefined {
    const m = viewMode.match(/p(\d*)/);
    if (!m) return undefined;
    return m[1] ? parseInt(m[1], 10) : PAUSED_INDEFINITELY;
}

/** The viewMode suffix for a pause end; '' when not paused. */
export function serializePausedUntil(pausedUntil: number | undefined): string {
    if (!pausedUntil) return '';
    return pausedUntil >= PAUSED_INDEFINITELY ? 'p' : `p${pausedUntil}`;
}

export function parseMarker(marker: string): ParsedMarker {
    const parts = marker.split('/');
    const feedId = parts[0];
    const sortOrder = parts[1] as MarkerSortOrder;
    const viewMode = parts[2];

    // Parse viewMode - backward compatible with single letter
    // Format: <showRead><compactMode>[m<maxUnread>][a<maxAgeDays>] where everything past showRead is optional
    const showRead = (viewMode[0] as ShowRead) || 'a';
    const compactMode = (viewMode.length > 1 && /[cfl]/.test(viewMode[1]) ? viewMode[1] as CompactMode : 'f') || 'f';
    const maxUnread = parseInt(viewMode.match(/m(\d+)/)?.[1] ?? '', 10) || undefined;
    const maxAgeDays = parseInt(viewMode.match(/a(\d+)/)?.[1] ?? '', 10) || undefined;
    const pausedUntil = parsePausedUntil(viewMode);
    const head = { feedId, sortOrder, showRead, compactMode, maxUnread, maxAgeDays, pausedUntil };

    const mode = parts[3] as MarkerMode;

    switch (mode) {
        case 'r':
            return { ...head, mode, readUntilIndex: parseInt(parts[4], 10) };
        case '0':
            return { ...head, mode };
        case 't':
            return { ...head, mode, readToIndex: parseInt(parts[4], 10) };
        case 'b':
            return {
                ...head, mode,
                firstPostInBitField: parseInt(parts[4], 10),
                lastPostInBitField: parseInt(parts[5], 10),
                restRead: parts[6] == '1'
            };
        default:
            throw new Error(`Unknown marker mode: ${mode}`);
    }
}

/** A read-only byte view, satisfied by both a Node `Buffer` (backend) and a `Uint8Array` (client). */
export type ByteArray = { readonly length: number; readonly [index: number]: number };

/**
 * Convert parsed marker + bitmap to a set of read post indices.
 * Only tracks the last MAX_TRACKED_POSTS posts (indices minTrackable to postCount-1).
 */
export function markerToReadSet(parsed: ParsedMarker, bitmap: ByteArray, postCount: number): Set<number> {
    const readPosts = new Set<number>();
    const minTrackable = Math.max(0, postCount - MAX_TRACKED_POSTS);

    switch (parsed.mode) {
        case 'r':
            // All posts 0 to readUntilIndex are read
            for (let i = Math.max(0, minTrackable); i <= (parsed.readUntilIndex ?? -1) && i < postCount; i++)
                readPosts.add(i);
            break;
        case '0':
            // No posts read
            break;
        case 't':
            // Posts 0 to readToIndex-1 are read
            for (let i = Math.max(0, minTrackable); i < (parsed.readToIndex ?? 0) && i < postCount; i++)
                readPosts.add(i);
            break;
        case 'b':
            // Bitfield mode: restRead determines posts outside bitmap range
            const first = parsed.firstPostInBitField ?? 0;
            const last = parsed.lastPostInBitField ?? 0;

            // Posts below the bitmap range (only if within trackable range)
            if (parsed.restRead)
                for (let i = minTrackable; i < last && i < postCount; i++)
                    readPosts.add(i);

            // Bitmap: first bit (bit 0) = highest index (first), last bit = lowest index (last)
            for (let i = 0; i < bitmap.length * 8; i++) {
                const byteIndex = Math.floor(i / 8);
                const bitIndex = i % 8;
                if (bitmap[byteIndex] & (1 << bitIndex)) {
                    const postIndex = first - i; // first bit = highest index
                    if (postIndex >= last && postIndex >= minTrackable && postIndex < postCount)
                        readPosts.add(postIndex);
                }
            }
            break;
    }

    return readPosts;
}
